import { useEffect, useRef, useState } from 'react';
import { Check, Lock, X } from 'lucide-react';
import { toast } from 'sonner';
import { learnAPI } from '../lib/api';
import { t } from '../lib/i18n';

// Comprehension check (s134, boards B2/B3) — the KMS Reading Quiz dynamic in the
// player: one question per screen, the answer + "Why" revealed by the SERVER after
// each pick (the key never ships to the browser), then a server-graded submit that
// completes the lesson only on a pass.
const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];

function Ring({ score, total, passed }) {
  const pct = total ? Math.round((100 * score) / total) : 0;
  return (
    <div
      className="flex h-[132px] w-[132px] items-center justify-center rounded-full"
      style={{ background: `conic-gradient(${passed ? 'var(--lms-accent)' : 'var(--lms-sale)'} 0 ${pct}%, var(--lms-line) ${pct}% 100%)` }}
    >
      <div className="flex h-[108px] w-[108px] flex-col items-center justify-center rounded-full bg-lms-surface">
        <span className="text-[30px] font-bold text-lms-ink lms-num">{score}/{total}</span>
        <span className="text-xs text-lms-sub">{t('correct')}</span>
      </div>
    </div>
  );
}

export default function LessonCheckModal({ lesson, lessonNumber, nextNumber, onClose, onPassed, onNext, onReview }) {
  const questions = lesson.check?.questions || [];
  const required = lesson.check?.required || 0;
  const [idx, setIdx] = useState(0);
  const [picks, setPicks] = useState({});        // {qid: index}
  const [feedback, setFeedback] = useState({});  // {qid: {correct, correct_index, explanation}}
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);
  const [reviewing, setReviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const primaryRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => e.key === 'Escape' && closeRef.current();
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const q = questions[idx];
  const fb = q ? feedback[q.id] : null;
  const correctSoFar = Object.values(feedback).filter((f) => f.correct).length;
  const answered = Object.keys(feedback).length;
  const last = idx === questions.length - 1;

  const pick = async (i) => {
    if (!q || fb || checking) return;
    setChecking(true);
    setPicks((p) => ({ ...p, [q.id]: i }));
    try {
      const { data } = await learnAPI.checkAnswer(lesson.id, q.id, i);
      setFeedback((f) => ({ ...f, [q.id]: data }));
      requestAnimationFrame(() => primaryRef.current?.focus());
    } catch (err) {
      setPicks((p) => { const n = { ...p }; delete n[q.id]; return n; });
      toast.error(err.response?.data?.detail || t('Could not check your answer'));
    } finally {
      setChecking(false);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await learnAPI.checkSubmit(lesson.id, picks);
      setResult(data);
      if (data.passed) onPassed(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Save failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const retry = () => {
    setIdx(0); setPicks({}); setFeedback({}); setResult(null); setReviewing(false);
  };

  const optionCls = (i) => {
    if (!fb) return 'border-lms-line bg-lms-surface hover:border-lms-ink';
    if (i === fb.correct_index) return 'border-[1.5px] border-lms-link bg-lms-tag-free-bg/60';
    if (i === picks[q.id]) return 'border-[1.5px] border-lms-sale bg-lms-tag-alt-bg/60';
    return 'border-lms-line bg-lms-surface opacity-70';
  };

  let body;
  if (result && reviewing) {
    body = (
      <div className="flex max-h-[60vh] flex-col gap-4 overflow-y-auto px-7 pb-2 pt-5">
        {questions.map((x, qi) => {
          const f = feedback[x.id] || {};
          return (
            <div key={x.id} className="flex flex-col gap-2 border-b border-lms-line pb-4 last:border-0">
              <span className="text-[15px] font-semibold text-lms-ink">{qi + 1}. {x.q}</span>
              {x.options.map((o, i) => (
                <span
                  key={i}
                  className={`rounded-lg px-3 py-1.5 text-sm ${i === f.correct_index ? 'bg-lms-tag-free-bg text-lms-tag-free-ink' : i === picks[x.id] ? 'bg-lms-tag-alt-bg text-lms-tag-alt-ink' : 'text-lms-sub'}`}
                >
                  {LETTERS[i]}. {o}
                  {i === f.correct_index ? ` — ${t('correct answer')}` : i === picks[x.id] ? ` — ${t('your answer')}` : ''}
                </span>
              ))}
              {f.explanation && <span className="text-[13px] leading-relaxed text-lms-body"><strong className="text-lms-ink">{t('Why')}:</strong> {f.explanation}</span>}
            </div>
          );
        })}
      </div>
    );
  } else if (result) {
    body = (
      <div className="flex flex-col items-center gap-3.5 px-8 pb-2 pt-4 text-center">
        <span className="text-xs font-bold uppercase tracking-[0.1em] text-lms-sub">{result.passed ? t('Passed') : t('Not passed yet')}</span>
        <Ring score={result.score} total={result.total} passed={result.passed} />
        <h2 id="check-title" className="mt-1 text-[22px] font-bold text-lms-ink">
          {result.passed ? t('Lesson complete') : t('Worth another look')}
        </h2>
        <p className="max-w-[400px] text-sm leading-relaxed text-lms-body">
          {result.passed
            ? (nextNumber ? `${t('Nice work. Lesson')} ${nextNumber} ${t('is unlocked.')}` : t('Nice work — you have finished this lesson.'))
            : `${t('You need')} ${result.required} ${t('of')} ${result.total} ${t('to complete this lesson. Go back over it, then try again — you can retry as many times as you need.')}`}
        </p>
        <span className={`inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-[13px] font-semibold ${result.passed ? 'bg-lms-tag-free-bg text-lms-tag-free-ink' : 'bg-lms-tag-alt-bg text-lms-tag-alt-ink'}`}>
          {result.passed ? <Check size={14} strokeWidth={2.6} /> : <Lock size={14} />}
          {result.passed ? t('Result saved to your progress') : (nextNumber ? `${t('Lesson')} ${nextNumber} ${t('stays locked')}` : t('This lesson stays incomplete'))}
        </span>
      </div>
    );
  } else if (q) {
    body = (
      <div className="flex flex-col gap-4 px-7 pb-2 pt-6">
        <span className="text-[17px] font-semibold leading-normal text-lms-ink">{idx + 1}. {q.q}</span>
        <div className="flex flex-col gap-2.5" role="group" aria-label={t('Answers')}>
          {q.options.map((o, i) => {
            const isRight = fb && i === fb.correct_index;
            const isWrongPick = fb && i === picks[q.id] && !fb.correct;
            return (
              <button
                key={i}
                type="button"
                onClick={() => pick(i)}
                disabled={Boolean(fb) || checking}
                className={`lms-press flex min-h-[52px] items-center gap-3.5 rounded-xl border px-4 py-2.5 text-left transition-colors ${optionCls(i)}`}
              >
                <span className={`flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-lg text-[13px] font-bold ${isRight ? 'bg-lms-link text-lms-accent-ink' : isWrongPick ? 'bg-lms-sale text-lms-accent-ink' : 'bg-lms-raised text-lms-sub'}`}>
                  {isRight ? <Check size={14} strokeWidth={3} /> : isWrongPick ? <X size={14} strokeWidth={3} /> : LETTERS[i]}
                </span>
                <span className="flex-1 text-[14.5px] text-lms-ink">{o}</span>
                {isRight && <span className="text-xs font-bold text-lms-tag-free-ink">{t('Correct answer')}</span>}
                {isWrongPick && <span className="text-xs font-bold text-lms-tag-alt-ink">{t('Your answer')}</span>}
              </button>
            );
          })}
        </div>
        {fb && (
          <div className="flex flex-col gap-1 rounded-xl bg-lms-raised px-4 py-3.5" aria-live="polite">
            <span className={`text-[13.5px] font-bold ${fb.correct ? 'text-lms-tag-free-ink' : 'text-lms-tag-alt-ink'}`}>
              {fb.correct ? t('Correct') : t('Not quite')}
            </span>
            {fb.explanation && (
              <span className="text-[13.5px] leading-relaxed text-lms-body"><strong className="text-lms-ink">{t('Why')}:</strong> {fb.explanation}</span>
            )}
          </div>
        )}
      </div>
    );
  }

  let footer;
  if (result && reviewing) {
    footer = (
      <div className="flex justify-end gap-2.5 px-7 pb-6 pt-4">
        <button type="button" onClick={() => setReviewing(false)} className="lms-press h-[46px] rounded-[10px] border border-lms-ink px-5 text-sm font-bold text-lms-ink hover:bg-lms-raised">
          {t('Back to results')}
        </button>
      </div>
    );
  } else if (result) {
    footer = (
      <div className="flex flex-wrap justify-center gap-2.5 px-7 pb-7 pt-4">
        {result.passed ? (
          <>
            <button type="button" onClick={() => setReviewing(true)} className="lms-press h-[46px] rounded-[10px] border border-lms-ink px-5 text-sm font-bold text-lms-ink hover:bg-lms-raised">
              {t('Review answers')}
            </button>
            <button ref={primaryRef} type="button" onClick={nextNumber ? onNext : onClose} className="lms-press h-[46px] rounded-[10px] bg-lms-link px-6 text-sm font-bold text-lms-accent-ink hover:brightness-110">
              {nextNumber ? `${t('Next lesson')} ›` : t('Close')}
            </button>
          </>
        ) : (
          <>
            <button type="button" onClick={retry} className="lms-press h-[46px] rounded-[10px] border border-lms-ink px-5 text-sm font-bold text-lms-ink hover:bg-lms-raised">
              {t('Try again')}
            </button>
            <button ref={primaryRef} type="button" onClick={onReview} className="lms-press h-[46px] rounded-[10px] bg-lms-link px-6 text-sm font-bold text-lms-accent-ink hover:brightness-110">
              {t('Review the lesson')}
            </button>
          </>
        )}
      </div>
    );
  } else {
    footer = (
      <div className="flex items-center justify-between gap-3 px-7 pb-6 pt-4">
        <span className="text-[13.5px] text-lms-sub lms-num">
          {answered ? `${correctSoFar} ${t('of')} ${answered} ${t('correct so far')}` : `${questions.length} ${t('questions')} · ${t('pass with')} ${required}`}
        </span>
        <button
          ref={primaryRef}
          type="button"
          disabled={!fb || submitting}
          onClick={() => (last ? submit() : setIdx(idx + 1))}
          className="lms-press h-[46px] rounded-[10px] bg-lms-link px-6 text-sm font-bold text-lms-accent-ink hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-45"
        >
          {submitting ? t('Saving…') : last ? t('See results') : t('Next question')}
        </button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/60 p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div role="dialog" aria-modal="true" aria-labelledby="check-title" className="flex w-full max-w-[620px] flex-col overflow-hidden rounded-2xl bg-lms-surface shadow-[var(--lms-elev-3)]">
        <div className="flex flex-col gap-3 px-7 pt-5">
          <div className="flex items-center justify-between">
            {result && !reviewing
              ? <span className="text-sm font-semibold text-lms-sub">{t('Check your understanding')}</span>
              : <h2 id="check-title" className="text-lg font-bold text-lms-ink">{reviewing ? t('Your answers') : t('Check your understanding')}</h2>}
            <button type="button" onClick={onClose} aria-label={t('Close')} className="-mr-3 flex h-11 w-11 items-center justify-center text-lms-sub hover:text-lms-ink">
              <X size={18} />
            </button>
          </div>
          {!result && (
            <>
              <div className="flex justify-between gap-4 text-[13px] text-lms-sub">
                <span className="lms-num">{t('Question')} {idx + 1} {t('of')} {questions.length}</span>
                <span className="truncate">{t('Lesson')} {lessonNumber} · {lesson.title}</span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-lms-line">
                <div className="h-1.5 bg-lms-accent transition-[width] duration-300 ease-out" style={{ width: `${((idx + (fb ? 1 : 0)) / questions.length) * 100}%` }} />
              </div>
            </>
          )}
        </div>
        {body}
        {footer}
      </div>
    </div>
  );
}
